// src/views/admin/InventoryManagementPage.js
// Enhanced Inventory Management Page with Modern UI/UX
//
// --- UPDATE (Inventory Enterprise v1) ----------------------------------------------------
//   • Real numbers: the header / statistics now come from FlowSense inventory intelligence
//     (items, value, items to re-order, stock-outs) — they stayed at 0 before.
//   • Low-stock alerts are loaded from the API and Dismiss really dismisses (it only logged).
//   • Export buttons work (CSV: all items with AI analytics, re-order list, fleet).
//   • New tabs: AI Intelligence (FlowSense AI + ML re-ordering, ABC × XYZ, stock movements),
//     Fleet & Vehicles (government cars, trucks, heavy equipment …), Parts ↔ Assets (link parts to
//     assets / asset types / vehicles / vehicle types; where used; parts for an asset).
//   • Live: refreshes when the WebSocket reports inventory_update / work-order completions.
//   Existing tabs and components are unchanged.
// --- END UPDATE ---------------------------------------------------------------------------
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Every tab gets the live FlowSense overview (`intel`) and follows WebSocket changes; the
//     header shows a live pill (connection, last change, refresh).
//   • New tab: Smart Reorder (FlowSense predictive procurement, scenario planner, draft PO).
//   • Low-stock alerts: FlowSense days of cover / suggested order, Quick reorder (Receive stock
//     movement), Create PO (Smart Reorder for that item), Dismiss all; they reload live.
//   • Quick actions menu works on every tab (it only logged), plus "Ask FlowSense AI" and
//     "Explain for the Ministry of Works" in a FlowSense drawer.
//   • The alerts are shown once (page header) — the Items tab no longer repeats them.
// --- END UPDATE ---------------------------------------------------------------------------
// --- UPDATE (Procurement MOW+ v1) ----------------------------------------------------------
//   • New tab: Purchase Orders (PurchaseOrderManager — FlowSense procurement AI, 3-stage
//     signatures, receiving into stock). Smart Reorder "Draft PO", low-stock "Create Purchase
//     Order" and Suppliers "Raise PO" open the PO wizard pre-filled; quick actions for the tab.
//   • Suppliers tab gets canManage, Raise PO and the "Add Supplier" quick action.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'; // --- UPDATE: + useCallback, useMemo, useRef
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  useColorModeValue,
  Flex,
  HStack,
  Icon,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  useToast, // --- UPDATE
} from '@chakra-ui/react';
import { 
  MdInventory, MdTrendingUp, MdTrendingDown, MdWarning, 
  MdRefresh, MdSettings, MdFileDownload, MdNotifications,
  MdLocalShipping, MdCategory, MdHistory, MdDashboard
} from 'react-icons/md';
import { motion } from 'framer-motion';
// --- UPDATE (Inventory Enterprise v1)
import { MdAutoAwesome, MdLocalShipping as MdFleet, MdLink, MdWifi } from 'react-icons/md';
import useInventoryIntel from '../../hooks/useInventoryIntel';
import inventoryIntelApi from '../../services/inventoryIntelApi';
import inventoryService from '../../services/inventoryService';
import { getUserInfo } from '../../utils/auth';
import { hasRole } from '../../config/roles';
import InventoryIntelTab from '../../components/inventory/InventoryIntelTab';
import FleetTab from '../../components/inventory/FleetTab';
import PartLinksTab from '../../components/inventory/PartLinksTab';
// --- END UPDATE
// --- UPDATE (Inventory MOW+ v2)
import { Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton } from '@chakra-ui/react';
import { MdShoppingCart } from 'react-icons/md';
import PredictiveReorderPanel from '../../components/inventory/PredictiveReorderPanel';
import StockMovementModal from '../../components/inventory/StockMovementModal';
import LiveIndicator from '../../components/inventory/LiveIndicator';
import FlowSenseInsightBar from '../../components/inventory/FlowSenseInsightBar';
import useInventoryLive, { socketConnected } from '../../hooks/useInventoryLive';
import inventoryCategoryService from '../../services/inventoryCategoryService';
import supplierService from '../../services/supplierService';
import { downloadCsv, stamp } from '../../utils/inventoryCsv';
import { categoryRollup } from '../../components/inventory/inventoryAiIntel';
// --- END UPDATE
// --- UPDATE (Procurement MOW+ v1)
import PurchaseOrderManager from '../../components/inventory/PurchaseOrderManager';
import { MdReceipt } from 'react-icons/md';
// --- END UPDATE

// Existing tab components
import InventoryItemsTab from '../../components/inventory/InventoryItemsTab';
import SuppliersTab from '../../components/inventory/SuppliersTab';
import CategoriesTab from '../../components/inventory/CategoriesTab';
import InventoryHistoryTab from '../../components/inventory/InventoryHistoryTab';

// New enhanced components
import InventoryStatisticsWidget from '../../components/inventory/InventoryStatisticsWidget';
import LowStockAlerts from '../../components/inventory/LowStockAlerts';
import QuickActionsMenu from '../../components/inventory/QuickActionsMenu';

// --- UPDATE (Inventory MOW+ v2 / Procurement MOW+ v1): constants after all imports (import/first)
const SCREEN_BY_TAB = ['items', 'suppliers', 'categories', 'history', 'intel', 'fleet', 'links', 'reorder', 'procurement']; // --- UPDATE (Procurement): + procurement
const WRITE_ACTIONS = new Set(['addItem', 'importItems', 'addCategory', 'registerVehicle', 'linkPart', 'addSupplier', 'raisePO']); // --- UPDATE (Procurement): + addSupplier, raisePO
const UNSUPPORTED_ACTIONS = new Set(['importSuppliers', 'setAlerts']); // not built yet — --- UPDATE (Procurement): addSupplier now works
const TAB_PO = 8; // --- UPDATE (Procurement)
// --- END UPDATE

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const InventoryManagementPage = () => {
  // Theme colors
  const tabColor = useColorModeValue("gray.600", "white");
  const tabSelectedColor = useColorModeValue("navy.700", "white");
  const tabSelectedBg = useColorModeValue("white", "navy.700");
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerGradient = useColorModeValue(
    'linear(to-r, teal.500, blue.600)',
    'linear(to-r, teal.400, blue.500)'
  );
  const tabBg = useColorModeValue("gray.100", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");

  // State for statistics (these would come from API in real implementation)
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    totalValue: 0,
    recentMovements: 0,
    needsReorder: 0,
  });

  const [activeTab, setActiveTab] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- UPDATE (Inventory Enterprise v1): live intelligence, alerts, lookups, exports ---
  const toast = useToast();
  const intel = useInventoryIntel();
  const me = useMemo(() => getUserInfo() || {}, []);
  const canManage = hasRole(me, '@office');
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [lookups, setLookups] = useState(null);
  const [riskFilter, setRiskFilter] = useState(null);
  const [linkItemFilter, setLinkItemFilter] = useState(null);
  const [openVehicleId, setOpenVehicleId] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadAlerts = useCallback(async () => {
    try {
      const r = await inventoryService.getLowStockAlerts();
      const list = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
      if (mountedRef.current) setLowStockAlerts(list);
    } catch (_) { /* alerts are admin/office only — hide quietly */ }
  }, []);
  const loadLookups = useCallback(async () => {
    try { const l = await inventoryIntelApi.lookups(); if (mountedRef.current) setLookups(l); } catch (_) { /* tabs show what they can */ }
  }, []);
  useEffect(() => { loadAlerts(); loadLookups(); }, [loadAlerts, loadLookups]);

  // real numbers for the header + statistics widget
  useEffect(() => {
    const t = intel.data?.totals;
    if (!t) return;
    setStats((s) => ({ ...s, totalItems: t.items, totalValue: t.value, lowStockItems: t.below_reorder, needsReorder: t.below_reorder, stockouts: t.stockouts, vehicles: t.vehicles }));
  }, [intel.data]);

  const handleDismissAlert = useCallback(async (id) => {
    try {
      await inventoryService.dismissLowStockAlert(id);
      setLowStockAlerts((l) => l.filter((a) => a.id !== id));
      toast({ title: 'Alert dismissed', status: 'success', duration: 2000 });
    } catch (e) { toast({ title: 'Could not dismiss', description: e?.message, status: 'error' }); }
  }, [toast]);

  const handleExport = useCallback(async (kind) => {
    try { await inventoryIntelApi.download(kind); toast({ title: 'Export ready', status: 'success', duration: 2000 }); }
    catch (e) { toast({ title: 'Export failed', description: e.message, status: 'error' }); }
  }, [toast]);

  const onIntelChanged = useCallback(() => { intel.reload({ quiet: true }); loadLookups(); loadAlerts(); }, [intel, loadLookups, loadAlerts]);
  // --- END UPDATE ---

  // --- UPDATE (Inventory MOW+ v2): live alerts / lookups, quick actions, Smart Reorder, FlowSense drawer
  const alertsLive = useInventoryLive(() => { loadAlerts(); loadLookups(); });
  const [command, setCommand] = useState(null);
  const [reorderSearch, setReorderSearch] = useState('');
  const [reorderKey, setReorderKey] = useState(0);
  const [aiDrawer, setAiDrawer] = useState(null);
  const [alertMove, setAlertMove] = useState(null);
  const sendCommand = useCallback((type, extra = {}) => setCommand({ type, at: Date.now(), ...extra }), []);
  // --- UPDATE (Procurement MOW+ v1): PO drafts from Smart Reorder / alerts / suppliers
  const [poDraft, setPoDraft] = useState(null);
  const [poPresetSupplier, setPoPresetSupplier] = useState(null);
  const openPODraft = useCallback((items) => { setPoDraft(items); setActiveTab(TAB_PO); }, []);
  const raisePOForSupplier = useCallback((s) => { setPoPresetSupplier(s?.id ?? null); setActiveTab(TAB_PO); }, []);
  // --- END UPDATE
  const liveInfo = useMemo(() => ({ connected: alertsLive.connected ?? socketConnected(), updatedAt: intel.updatedAt, lastEvent: intel.lastEvent || alertsLive.lastEvent, onRefresh: intel.refresh }), [alertsLive.connected, alertsLive.lastEvent, intel.updatedAt, intel.lastEvent, intel.refresh]);
  const handleAlertReorder = useCallback((a) => {
    if (a.item_id == null) { toast({ title: 'Open the item to receive stock', status: 'info' }); return; }
    setAlertMove({ id: a.item_id, name: a.name, sku: a.sku || a.insight?.sku, quantity: a.quantity, location: a.warehouse_location || a.insight?.location, effective_reorder_point: a.insight?.effective_reorder_point });
  }, [toast]);
  // --- UPDATE (Procurement MOW+ v1): Create Purchase Order opens the PO wizard with the item and the FlowSense quantity (was: Smart Reorder search)
  const handleCreatePO = useCallback((a) => {
    const ai = a?.insight || null;
    if (a?.item_id == null) { setReorderSearch(a?.name || ''); setActiveTab(7); return; }
    const qty = Math.max(1, Math.round(ai?.suggested_order_net ?? ai?.suggested_order_qty ?? ai?.eoq ?? 1) || 1);
    openPODraft([{ item_id: a.item_id, name: a.name, quantity: qty, unit_price: ai?.unit_price ?? null, supplier_id: null }]);
  }, [openPODraft]);
  // --- END UPDATE
  const handleDismissAll = useCallback(async (ids) => {
    for (const id of ids) { try { await inventoryService.dismissLowStockAlert(id); } catch (_) { /* keep going */ } }
    loadAlerts(); toast({ title: 'Alerts dismissed', status: 'success', duration: 2000 });
  }, [loadAlerts, toast]);
  const exportCategories = useCallback(async () => {
    try {
      const r = await inventoryCategoryService.getAllCategories(); const cats = Array.isArray(r) ? r : r?.data || [];
      const roll = categoryRollup(cats, intel.data?.items || null).byName;
      downloadCsv(`flowsense-categories-${stamp()}.csv`, ['id', 'name', 'description', 'items', 'value', 'to_reorder'], cats.map((c) => [c.id, c.name, c.description || '', roll[c.name]?.items ?? '', roll[c.name]?.hasValue ? roll[c.name].value.toFixed(2) : '', roll[c.name]?.below_reorder ?? '']));
    } catch (e) { toast({ title: 'Export failed', description: e.message, status: 'error' }); }
  }, [intel.data, toast]);
  const exportSuppliers = useCallback(async () => {
    try {
      const r = await supplierService.getAllSuppliers(); const list = Array.isArray(r) ? r : r?.data || [];
      const keys = Array.from(new Set(list.flatMap((x) => Object.keys(x)))).filter((k) => typeof list[0]?.[k] !== 'object');
      downloadCsv(`flowsense-suppliers-${stamp()}.csv`, keys, list.map((x) => keys.map((k) => x[k])));
    } catch (e) { toast({ title: 'Export failed', description: e.message, status: 'error' }); }
  }, [toast]);
  const isActionSupported = useCallback((a) => !UNSUPPORTED_ACTIONS.has(a) && (canManage || !WRITE_ACTIONS.has(a)), [canManage]);
  const handleQuickAction = useCallback((action, tab) => {
    switch (action) {
      case 'askAi': setAiDrawer({ tab, auto: null, at: Date.now() }); return;
      case 'explainMinistry': setAiDrawer({ tab, auto: 'ministry', at: Date.now() }); return;
      case 'createPO': setActiveTab(TAB_PO); setTimeout(() => sendCommand('raisePO'), 0); return; // --- UPDATE (Procurement): opens the PO wizard (was Smart Reorder)
      case 'exportReorder': handleExport('reorder'); return;
      case 'exportFleet': handleExport('fleet'); return;
      case 'exportInventory': if (tab === 0) sendCommand('exportInventory'); else handleExport('items'); return;
      case 'refreshAi': intel.refresh(); toast({ title: 'FlowSense analysis refreshed', status: 'info', duration: 1500 }); return;
      case 'refreshReorder': setReorderKey((k) => k + 1); return;
      case 'exportCategories': exportCategories(); return;
      case 'exportSuppliers': exportSuppliers(); return;
      case 'printReport': window.print(); return;
      default: sendCommand(action);
    }
  }, [handleExport, sendCommand, intel, toast, exportCategories, exportSuppliers]);
  // --- END UPDATE

  // Handler for refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    intel.refresh(); loadAlerts(); loadLookups(); // --- UPDATE: really refresh
    // Trigger refresh in child components
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  // Tab configuration with icons and badges
  const tabs = [
    { name: 'Inventory Items', icon: MdInventory, badge: stats.totalItems },
    { name: 'Suppliers', icon: MdLocalShipping, badge: null },
    { name: 'Categories', icon: MdCategory, badge: null },
    { name: 'Audit History', icon: MdHistory, badge: stats.recentMovements },
    // --- UPDATE (Inventory Enterprise v1)
    { name: 'AI Intelligence', icon: MdAutoAwesome, badge: intel.data?.totals?.below_reorder || null },
    { name: 'Fleet & Vehicles', icon: MdFleet, badge: intel.data?.totals?.vehicles || null },
    { name: 'Parts ↔ Assets', icon: MdLink, badge: intel.data?.totals?.linked_parts || null },
    { name: 'Smart Reorder', icon: MdShoppingCart, badge: intel.data?.totals?.stockouts || null }, // --- UPDATE (MOW+ v2)
    { name: 'Purchase Orders', icon: MdReceipt, badge: null }, // --- UPDATE (Procurement MOW+ v1)
    // --- END UPDATE
  ];

  // --- UPDATE: FlowSense finding actions jump to the right tab
  const TAB_AI = 4; const TAB_FLEET = 5; const TAB_LINKS = 6;
  const handleAiAction = useCallback((a) => {
    if (a.type === 'vehicle') { setOpenVehicleId(a.vehicle_id); setActiveTab(TAB_FLEET); }
    else if (a.type === 'filter') { setRiskFilter(a.risk || null); setActiveTab(TAB_AI); }
  }, []);
  const handleOpenLinks = useCallback((item) => { setLinkItemFilter({ id: item.id, name: item.name }); setActiveTab(TAB_LINKS); }, []);
  // --- END UPDATE

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} p={{ base: '20px', md: '30px' }}>
      {/* ====================== ENHANCED HEADER ====================== */}
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
          <HStack spacing={3}>
            {/* Animated gradient icon */}
            <MotionBox
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, type: 'spring' }}
            >
              <Box
                w="56px"
                h="56px"
                bgGradient={headerGradient}
                borderRadius="xl"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="lg"
              >
                <Icon as={MdInventory} color="white" boxSize={7} />
              </Box>
            </MotionBox>

            <Box>
              <HStack spacing={2} align="center">
                <Text 
                  fontSize={{ base: "24px", md: "30px" }} 
                  fontWeight="800"
                  bgGradient={headerGradient}
                  bgClip="text"
                >
                  Inventory Dashboard
                </Text>
                {stats.lowStockItems > 0 && (
                  <Tooltip label={`${stats.lowStockItems} items need attention`} hasArrow>
                    <Badge 
                      colorScheme="red" 
                      fontSize="xs"
                      borderRadius="full"
                      px={3}
                      py={1}
                      cursor="pointer"
                    >
                      <HStack spacing={1}>
                        <Icon as={MdWarning} boxSize={3} />
                        <Text>{stats.lowStockItems} Low</Text>
                      </HStack>
                    </Badge>
                  </Tooltip>
                )}
              </HStack>
              <Text color={mutedTextColor} fontSize="sm" fontWeight="500">
                Complete overview of your assets, procurement, and audit trail
              </Text>
              {/* --- UPDATE: live status */}
              <HStack spacing={1} mt={0.5}>
                <Icon as={MdWifi} color={intel.error ? 'orange.400' : 'green.400'} boxSize={3} />
                <Text fontSize="xs" color={mutedTextColor}>
                  {intel.error ? 'FlowSense intelligence unavailable' : intel.updatedAt ? `Live · FlowSense AI updated ${intel.updatedAt.toLocaleTimeString()}` : 'Connecting to FlowSense AI…'}
                </Text>
                {intel.lastEvent && <Text fontSize="xs" color={mutedTextColor}>· last change {intel.lastEvent.at.toLocaleTimeString()}</Text>}{/* --- UPDATE (MOW+ v2) */}
              </HStack>
            </Box>
          </HStack>

          {/* Action buttons */}
          <HStack spacing={2}>
            <LiveIndicator connected={liveInfo.connected} updatedAt={liveInfo.updatedAt} lastEvent={liveInfo.lastEvent} />{/* --- UPDATE (MOW+ v2) */}
            <Tooltip label="Refresh data" hasArrow>
              <IconButton
                icon={<Icon as={MdRefresh} />}
                size="md"
                variant="outline"
                onClick={handleRefresh}
                isLoading={isRefreshing}
                borderRadius="xl"
                aria-label="Refresh"
              />
            </Tooltip>

            <Tooltip label="Export reports" hasArrow>
              <IconButton
                icon={<Icon as={MdFileDownload} />}
                size="md"
                variant="outline"
                borderRadius="xl"
                aria-label="Export"
                onClick={() => handleExport('items')} // --- UPDATE: was a no-op
              />
            </Tooltip>

            <Menu>
              <Tooltip label="Quick actions" hasArrow>
                <MenuButton
                  as={IconButton}
                  icon={<Icon as={MdSettings} />}
                  size="md"
                  variant="outline"
                  borderRadius="xl"
                  aria-label="Settings"
                />
              </Tooltip>
              <MenuList borderRadius="xl" boxShadow="xl">
                <MenuItem icon={<Icon as={MdDashboard} />} borderRadius="lg">
                  Manage Dashboard
                </MenuItem>
                <MenuItem icon={<Icon as={MdNotifications} />} borderRadius="lg">
                  Alert Settings
                </MenuItem>
                <MenuItem icon={<Icon as={MdFileDownload} />} borderRadius="lg" onClick={() => handleExport('items')}>
                  Export All Data
                </MenuItem>
                {/* --- UPDATE */}
                <MenuItem icon={<Icon as={MdFileDownload} />} borderRadius="lg" onClick={() => handleExport('reorder')}>Export Re-order List</MenuItem>
                <MenuItem icon={<Icon as={MdFileDownload} />} borderRadius="lg" onClick={() => handleExport('fleet')}>Export Fleet</MenuItem>
                <MenuItem icon={<Icon as={MdAutoAwesome} />} borderRadius="lg" onClick={() => setActiveTab(TAB_AI)}>FlowSense AI Intelligence</MenuItem>
                <MenuItem icon={<Icon as={MdShoppingCart} />} borderRadius="lg" onClick={() => setActiveTab(7)}>Smart Reorder</MenuItem>{/* --- UPDATE (MOW+ v2) */}
                <MenuItem icon={<Icon as={MdAutoAwesome} />} borderRadius="lg" onClick={() => setAiDrawer({ tab: activeTab, auto: 'ministry', at: Date.now() })}>Explain for the Ministry of Works</MenuItem>{/* --- UPDATE (MOW+ v2) */}
                {/* --- END UPDATE */}
              </MenuList>
            </Menu>
          </HStack>
        </Flex>

        {/* ====================== STATISTICS WIDGET ====================== */}
        <Box mb={6}>
          <InventoryStatisticsWidget 
            stats={stats} 
            onStatsUpdate={setStats}
            isRefreshing={isRefreshing}
            intel={intel.data} // --- UPDATE (MOW+ v2): AI health score, stock-outs, re-order value
          />
        </Box>

        {/* ====================== LOW STOCK ALERTS ====================== */}
        {(lowStockAlerts.length > 0) && ( /* --- UPDATE: real alerts from the API */
          <Box mb={6}>
            <LowStockAlerts 
              alerts={lowStockAlerts} // --- UPDATE: populated from GET /inventory/alerts/low-stock
              onDismiss={handleDismissAlert} // --- UPDATE: really dismisses
              insights={intel.data?.items || null} // --- UPDATE (MOW+ v2): FlowSense per alert
              onReorder={canManage ? handleAlertReorder : null} // --- UPDATE (MOW+ v2)
              onCreatePO={handleCreatePO} // --- UPDATE (MOW+ v2)
              onDismissAll={handleDismissAll} // --- UPDATE (MOW+ v2)
            />
          </Box>
        )}
      </MotionBox>

      {/* ====================== ENHANCED TABS ====================== */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Tabs 
          isFitted 
          variant="soft-rounded" 
          colorScheme="blue"
          index={activeTab}
          onChange={setActiveTab}
          isLazy // --- UPDATE: heavy tabs (fleet, intelligence) render only when opened
        >
          <TabList 
            mb={6}
            bg={tabBg} 
            borderRadius="2xl" 
            p={3}
            borderWidth="2px"
            borderColor={borderColor}
            boxShadow="md"
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                _selected={{ 
                  color: tabSelectedColor, 
                  bg: tabSelectedBg,
                  boxShadow: 'md'
                }}
                color={tabColor}
                borderRadius="xl"
                fontWeight="600"
                fontSize="sm"
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'sm'
                }}
              >
                <HStack spacing={2}>
                  <Icon as={tab.icon} boxSize={4} />
                  <Text display={{ base: 'none', md: 'block' }}>
                    {tab.name}
                  </Text>
                  <Text display={{ base: 'block', md: 'none' }}>
                    {tab.name.split(' ')[0]}
                  </Text>
                  {tab.badge > 0 && (
                    <Badge 
                      colorScheme="blue" 
                      borderRadius="full"
                      fontSize="10px"
                    >
                      {tab.badge}
                    </Badge>
                  )}
                </HStack>
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <InventoryItemsTab refreshTrigger={isRefreshing} intel={intel.data} lookups={lookups} canManage={canManage} hideAlerts onOpenLinks={handleOpenLinks} onCreatePO={handleCreatePO} onChanged={onIntelChanged} command={activeTab === 0 ? command : null} />{/* --- UPDATE (MOW+ v2) */}
            </TabPanel>
            <TabPanel px={0}>
              <SuppliersTab refreshTrigger={isRefreshing} canManage={canManage} onRaisePO={raisePOForSupplier} command={activeTab === 1 ? command : null} />{/* --- UPDATE (Procurement) */}
            </TabPanel>
            <TabPanel px={0}>
              <CategoriesTab refreshTrigger={isRefreshing} intel={intel.data} canManage={canManage} command={activeTab === 2 ? command : null} />{/* --- UPDATE (MOW+ v2) */}
            </TabPanel>
            <TabPanel px={0}>
              <InventoryHistoryTab refreshTrigger={isRefreshing} intel={intel.data} command={activeTab === 3 ? command : null} />{/* --- UPDATE (MOW+ v2) */}
            </TabPanel>
            {/* --- UPDATE (Inventory Enterprise v1) */}
            <TabPanel px={0}>
              <InventoryIntelTab data={intel.data} isLoading={intel.isLoading} error={intel.error} lookups={lookups}
                riskFilter={riskFilter} onRiskFilter={setRiskFilter} onAction={handleAiAction} onOpenLinks={handleOpenLinks} onChanged={onIntelChanged} live={liveInfo} />{/* --- UPDATE (MOW+ v2): + live */}
            </TabPanel>
            <TabPanel px={0}>
              <FleetTab data={intel.data} lookups={lookups} canManage={canManage} openVehicleId={openVehicleId} onVehicleOpened={() => setOpenVehicleId(null)} onChanged={onIntelChanged} command={activeTab === 5 ? command : null} />{/* --- UPDATE (MOW+ v2): + command */}
            </TabPanel>
            <TabPanel px={0}>
              <PartLinksTab lookups={lookups} canManage={canManage} itemFilter={linkItemFilter} onClearItemFilter={() => setLinkItemFilter(null)} onChanged={onIntelChanged} intel={intel.data} command={activeTab === 6 ? command : null} />{/* --- UPDATE (MOW+ v2): + intel, command */}
            </TabPanel>
            {/* --- UPDATE (MOW+ v2): Smart Reorder */}
            <TabPanel px={0}>
              <PredictiveReorderPanel key={reorderKey} intel={intel.data} initialSearch={reorderSearch} onDraftPO={openPODraft} />{/* --- UPDATE (Procurement): Draft PO → PO wizard */}
            </TabPanel>
            {/* --- UPDATE (Procurement MOW+ v1): Purchase Orders */}
            <TabPanel px={0}>
              <PurchaseOrderManager aiDraftItems={poDraft} clearAiDraft={() => setPoDraft(null)} presetSupplierId={poPresetSupplier} clearPresetSupplier={() => setPoPresetSupplier(null)} command={activeTab === TAB_PO ? command : null} />
            </TabPanel>
            {/* --- END UPDATE */}
          </TabPanels>
        </Tabs>
      </MotionBox>

      {/* ====================== QUICK ACTIONS MENU ====================== */}
      <QuickActionsMenu activeTab={activeTab} onAction={handleQuickAction} isSupported={isActionSupported} badge={intel.data?.totals?.below_reorder || null} />{/* --- UPDATE (MOW+ v2): actions work */}

      {/* --- UPDATE (MOW+ v2): Receive stock from a low-stock alert + FlowSense drawer */}
      <StockMovementModal isOpen={!!alertMove} onClose={() => setAlertMove(null)} item={alertMove} lookups={lookups} defaultType="receive"
        onDone={(r) => { toast({ title: 'Stock received', description: `${r.item_name}: ${r.quantity_before} → ${r.quantity_after}`, status: 'success', duration: 3000 }); onIntelChanged(); }} />
      <Drawer isOpen={!!aiDrawer} onClose={() => setAiDrawer(null)} size="md" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px"><HStack><Icon as={MdAutoAwesome} color="teal.400" /><Text>FlowSense AI · Inventory</Text></HStack></DrawerHeader>
          <DrawerBody py={4}>
            {aiDrawer && (
              <FlowSenseInsightBar key={aiDrawer.at} screen={SCREEN_BY_TAB[aiDrawer.tab] || 'items'} data={intel.data} defaultOpen autoExplain={aiDrawer.auto}
                headline={intel.data?.totals ? `Health ${intel.data.score}/100 · ${intel.data.totals.below_reorder} to re-order · ${intel.data.totals.vehicles} vehicles` : null} />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {/* --- END UPDATE */}
    </Box>
  );
};

export default InventoryManagementPage;